import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatNOK, formatPercent } from "@/lib/format";
import type { ProjectBfEntry, ProjectDeviationEntry } from "@/lib/dashboard-panels-calc";
import type { ProjectMeta } from "@/hooks/use-sales-dashboard-data";
import { TONE_TEXT } from "@/lib/widget-status";
import { cn } from "@/lib/utils";

interface BestWorstProjectsPanelProps {
  top: ProjectBfEntry[];
  worst: ProjectDeviationEntry[];
  metaOf: (projectId: string) => ProjectMeta | undefined;
  isLoading?: boolean;
}

function label(meta: ProjectMeta | undefined): string {
  if (!meta) return "Ukjent prosjekt";
  return meta.code ? `${meta.code} · ${meta.name}` : meta.name;
}

/**
 * F2.3 Best / worst projects — highest realised BF and largest budget deviation
 * (worst first), for completed sales projects in the period (period + person
 * filtered upstream).
 */
export function BestWorstProjectsPanel({ top, worst, metaOf, isLoading }: BestWorstProjectsPanelProps) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Beste prosjekter (GEEK)</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-28 animate-pulse rounded bg-muted" />
          ) : top.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ingen fullførte prosjekter i perioden.</p>
          ) : (
            <ul className="space-y-1">
              {top.map((p) => (
                <li key={p.projectId} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate">{label(metaOf(p.projectId))}</span>
                  <span className="tabular-nums">{formatNOK(p.bfActualKr)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Størst budsjettavvik</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-28 animate-pulse rounded bg-muted" />
          ) : worst.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ingen fullførte prosjekter i perioden.</p>
          ) : (
            <ul className="space-y-1">
              {worst.map((p) => (
                <li key={p.projectId} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate">{label(metaOf(p.projectId))}</span>
                  <span
                    className={cn("tabular-nums", TONE_TEXT[p.deviationKr < 0 ? "miss" : "reached"])}
                  >
                    {formatPercent(p.deviationPct)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
